import AppleStoreButton from 'assets/images/getVaroApp/AppleStoreIcon.svg';
import GooglePlayButton from 'assets/images/getVaroApp/GooglePlayIcon.svg';
import qrCodeImage from 'assets/images/getVaroApp/varo-app-qr-code.png';
import { Stack } from 'expo-router';
import SVGButton from 'features/getVaroApp/components/SVGButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image,
  ImageSourcePropType,
  Linking,
  StyleSheet,
  View,
} from 'react-native';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import {
  ANDROID_APP_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
} from 'shared/constants/appStore';
import { isAndroidBrowser, isMobileBrowser } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import theme from 'shared/styles/theme';

const ZelleScreen = () => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'zelle',
  });

  const trackMobileAppDownloadClicked = usePressableAnalytics({
    analyticsId: 'mobile_app_download_clicked',
    customAttributes: {
      error_type: 'app_required',
    },
  });

  if (isMobileBrowser) {
    return (
      <View style={styles.container}>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.topSpacer} />
        <NavigationBar title={undefined} primaryOnPress={navigateBack} />
        <View style={styles.navSpacer} />
        <ActionScreen
          visual="PHONE_B"
          titleText={translatedText('web.title')}
          titleTextStyle="extra-large"
          bodyContent={
            <BodyBaseText>{translatedText('web.subtitle')}</BodyBaseText>
          }
          primaryButton={{
            label: translatedText('web.getAppCta'),
            onPress: () => {
              trackMobileAppDownloadClicked();
              void Linking.openURL(
                isAndroidBrowser
                  ? ANDROID_APP_PLAY_STORE_URL
                  : IOS_APP_STORE_URL,
              );
            },
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={styles.topSpacer} />
      <NavigationBar title={undefined} primaryOnPress={navigateBack} />
      <View style={styles.navSpacer} />
      <View style={styles.desktopContainer}>
        <TitleXLText accessibilityRole="header" style={styles.desktopTitle}>
          {translatedText('web.title')}
        </TitleXLText>
        <BodyBaseText style={styles.desktopSubtitle}>
          {translatedText('web.subtitle')}
        </BodyBaseText>
        <BodyBaseBoldText style={styles.desktopDescription}>
          {translatedText('web.description')}
        </BodyBaseBoldText>

        <View style={styles.qrSection}>
          <View style={styles.qrCodeContainer}>
            <Image
              source={qrCodeImage as ImageSourcePropType}
              style={styles.qrCodeImage}
              accessibilityRole="image"
              accessible
              accessibilityLabel={translatedText('web.qrCodeLabel')}
              resizeMode="contain"
            />
          </View>
          <View style={styles.storeButtonsContainer}>
            <SVGButton
              SVGComponent={<AppleStoreButton />}
              onPress={() => {
                trackMobileAppDownloadClicked();
                void Linking.openURL(IOS_APP_STORE_URL);
              }}
              testID="apple-store-button"
            />
            <SVGButton
              SVGComponent={<GooglePlayButton />}
              onPress={() => {
                trackMobileAppDownloadClicked();
                void Linking.openURL(ANDROID_APP_PLAY_STORE_URL);
              }}
              testID="google-play-button"
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  desktopContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.m,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  desktopTitle: {
    marginBottom: theme.spacing.s,
  },
  desktopSubtitle: {
    marginBottom: theme.spacing.l,
  },
  desktopDescription: {
    marginBottom: theme.spacing.xl,
  },
  navSpacer: {
    height: theme.spacing.m,
  },
  topSpacer: {
    height: theme.spacing.l,
  },
  qrSection: {
    backgroundColor: theme.color.background.surface,
    borderRadius: theme.spacing.xs,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xxl,
    alignItems: 'center',
    gap: theme.spacing.xxl,
  },
  qrCodeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 120,
    height: 120,
  },
  qrCodeImage: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  storeButtonsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
});

export default ZelleScreen;
